import { supabase } from "../config/supabase.ts";
import * as FinancialTypes from "../types/unified-financial.ts";
import { InvoiceService } from "./InvoiceService.ts";

// Idealmente, os tipos para "Insert" e "Update" viriam do codegen do Supabase
type PackageInsert = Omit<FinancialTypes.PackageInfo, 'packageId' | 'packageCreatedAt'>;
type PackageUpdate = Partial<PackageInsert>;

const toDateOnly = (date: Date): string => date.toISOString().split("T")[0];

const validityMonthsFromPackage = (pkg: {
  packageValidityType?: string | null;
  packageCustomValidityMonths?: number | null;
}): number => {
  switch (pkg.packageValidityType) {
    case "1_MONTH":
      return 1;
    case "2_MONTHS":
      return 2;
    case "3_MONTHS":
      return 3;
    case "CUSTOM":
      return Number(pkg.packageCustomValidityMonths || 0);
    default:
      return 0;
  }
};

const addMonthsClamped = (baseDate: Date, monthsToAdd: number): Date => {
  const targetMonthStart = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth() + monthsToAdd,
    1
  );

  const lastDayOfTargetMonth = new Date(
    targetMonthStart.getFullYear(),
    targetMonthStart.getMonth() + 1,
    0
  ).getDate();

  const clampedDay = Math.min(baseDate.getDate(), lastDayOfTargetMonth);

  return new Date(
    targetMonthStart.getFullYear(),
    targetMonthStart.getMonth(),
    clampedDay
  );
};


/**
 * Cria um novo pacote no banco de dados.
 */
const createPackage = async (packageData: PackageInsert): Promise<FinancialTypes.PackageInfo> => {
  console.log("📦 Criando novo pacote no Supabase...", packageData);

  // 1. Inserir o novo pacote no banco de dados
  const { data: newPackage, error } = await supabase
    .from("packages")
    .insert(packageData)
    .select()
    .single();

  if (error) {
    console.error("❌ Erro ao criar pacote:", error);
    throw new Error(error.message);
  }

  console.log(`✅ Pacote ${newPackage.packageId} criado com sucesso.`);

  // 2. Gerar a cobrança (invoice) para o pacote recém-criado
  try {
    console.log(`🧾 Disparando faturamento para o pacote ${newPackage.packageId}...`);

    // Para faturar, precisamos do patientId e clientId, que obtemos a partir do financialProfileId
    const { data: profileData, error: profileError } = await supabase
      .from("financial_profiles")
      .select("patientId, clientId")
      .eq("financialProfileId", newPackage.financialProfileId)
      .single();

    if (profileError || !profileData) {
      throw new Error(`Não foi possível encontrar o perfil financeiro para o faturamento (ID: ${newPackage.financialProfileId}).`);
    }

    await InvoiceService.findOrCreateInvoiceAndAddItem({
      patientId: profileData.patientId,
      clientId: profileData.clientId,
      description: `Pacote de Fisioterapia: ${newPackage.packageName || 'Padrão'}`, // Garante que há um nome
      amount: newPackage.packagePrice,
      type: 'package',
      date: new Date(newPackage.packageCreatedAt)
    });

  } catch (billingError: any) {
    // Se o faturamento falhar, o pacote já foi criado. 
    // É importante logar este erro para uma possível reconciliação manual.
    console.error(`🚨 ALERTA: O pacote ${newPackage.packageId} foi criado, mas o faturamento falhou:`, billingError.message);
    // Dependendo da criticidade, pode-se querer lançar o erro ou apenas retornar o pacote.
    // Por enquanto, vamos logar e continuar, para não impedir o fluxo principal na UI.
  }

  return newPackage as FinancialTypes.PackageInfo;
};

/**
 * Atualiza um pacote existente.
 */
const updatePackage = async (
  packageId: string,
  packageUpdate: PackageUpdate,
  clientId?: string
): Promise<FinancialTypes.PackageInfo> => {
  console.log(`📦 Atualizando pacote ${packageId} no Supabase...`, packageUpdate);

  let query = supabase
    .from("packages")
    .update(packageUpdate)
    .eq("packageId", packageId);

  if (clientId) {
    query = query.eq("clientId", clientId);
  }

  const { data, error } = await query.select().single();

  if (error) {
    console.error(`❌ Erro ao atualizar pacote ${packageId}:`, error);
    throw new Error(error.message);
  }

  return data as FinancialTypes.PackageInfo;
};

/**
 * Consome uma sessão de um pacote, associando-a a um agendamento.
 * Utiliza uma RPC no Supabase para garantir a atomicidade da operação.
 */
const consumeSession = async (packageId: string, appointmentId: string): Promise<FinancialTypes.PackageSessionUsage> => {
  console.log(`🧾 Consumindo sessão do pacote ${packageId} para o agendamento ${appointmentId} via RPC...`);

  const { data, error } = await supabase.rpc("consume_package_session", {
    p_package_id: packageId,
    p_appointment_id: appointmentId,
  });

  if (error) {
    console.error("❌ Erro ao consumir sessão do pacote:", error);
    throw new Error(error.message);
  }

  // Garantia defensiva no código da aplicação: mantém o status explícito como "active"
  // mesmo quando o insert ocorre dentro da RPC.
  const usageId =
    (data as any)?.usageId ??
    (Array.isArray(data) ? (data[0] as any)?.usageId : undefined) ??
    (data as any)?.packageSessionUsageId ??
    (Array.isArray(data) ? (data[0] as any)?.packageSessionUsageId : undefined);

  if (usageId) {
    const { error: normalizeUsageStatusError } = await supabase
      .from("package_session_usage")
      .update({ usageStatus: "active" })
      .eq("usageId", usageId)
      .eq("packageId", packageId)
      .eq("appointmentId", appointmentId);

    if (normalizeUsageStatusError) {
      console.warn(
        "⚠️ Não foi possível normalizar usageStatus='active' após RPC consume_package_session:",
        normalizeUsageStatusError.message
      );
    }
  }

  // A função RPC deve retornar o registro de 'package_session_usage' criado.
  return data;
};

/**
 * Adiciona créditos (sessões) a um pacote existente e gera a cobrança correspondente.
 */
const addCredits = async (
    packageId: string, 
    sessionsAdded: number, 
    creditPrice: number
): Promise<FinancialTypes.PackageCreditAddition> => {
    console.log(`💰 Iniciando adição de ${sessionsAdded} sessões ao pacote ${packageId}...`);

    // 1. Buscar dados essenciais do pacote e do perfil financeiro associado
    const { data: packageData, error: pkgError } = await supabase
        .from("packages")
        .select(`
            packageId,
            clientId,
            financialProfileId,
            packagePrice,
            packageSessionsContracted,
            packageSessionsRemaining,
            packageValidityType,
            packageCustomValidityMonths,
            financial_profiles (
                patientId
            )
        `)
        .eq("packageId", packageId)
        .single();

    if (pkgError || !packageData) {
        console.error("❌ Erro ao buscar pacote para adicionar créditos:", pkgError);
        throw new Error(pkgError?.message || "Pacote não encontrado.");
    }

    // Acessando o perfil financeiro. O Supabase pode retornar um array mesmo para relações to-one.
    const financialProfile = Array.isArray(packageData.financial_profiles) 
        ? packageData.financial_profiles[0]
        : packageData.financial_profiles;

    if (!financialProfile) {
        throw new Error("Não foi possível encontrar o perfil financeiro associado ao pacote.");
    }

    const patientId = financialProfile.patientId;
    if (!patientId) {
        throw new Error("Não foi possível identificar o paciente associado ao pacote.");
    }

    // 2. Usar o InvoiceService para criar o item de fatura e obter o invoiceId
    const { invoiceId } = await InvoiceService.findOrCreateInvoiceAndAddItem({
        patientId: patientId,
        clientId: packageData.clientId,
        description: `Crédito de ${sessionsAdded} sessão(ões) extra(s)`,
        amount: creditPrice,
        type: 'additional',
    });

    console.log(`🧾 Faturamento do crédito gerado na fatura ${invoiceId}.`);

    // 3. Atualizar pacote como renovado hoje + incrementos de créditos
    const today = new Date();
    const todayDateOnly = toDateOnly(today);
    const validityMonths = validityMonthsFromPackage(packageData);
    const renewalDate =
      validityMonths > 0
        ? toDateOnly(addMonthsClamped(today, validityMonths))
        : todayDateOnly;

    const updatedPackage = {
        packagePaymentDate: todayDateOnly,
        packageRenewalDate: renewalDate,
        packageSessionsContracted: Number(packageData.packageSessionsContracted || 0) + sessionsAdded,
        packageSessionsRemaining: Number(packageData.packageSessionsRemaining || 0) + sessionsAdded,
        packagePrice: Number(packageData.packagePrice || 0) + creditPrice,
    };

    const { error: updatePkgError } = await supabase
        .from("packages")
        .update(updatedPackage)
        .eq("packageId", packageId)
        .eq("clientId", packageData.clientId);

    if (updatePkgError) {
        console.error("❌ Erro ao atualizar os contadores do pacote:", updatePkgError);
        // Num cenário ideal, teríamos um rollback para o item da fatura criado.
        // Por enquanto, lançamos o erro.
        throw new Error(updatePkgError.message);
    }

    // 4. Registrar a adição de crédito na tabela `package_credit_additions`
    const creditAdditionPayload = {
        clientId: packageData.clientId,
        packageId: packageId,
        invoiceId: invoiceId,
        sessionsAdded: sessionsAdded,
        additionNotes: `Faturado na invoice ${invoiceId}`,
    };

    const { data: newCreditAddition, error: creditError } = await supabase
        .from("package_credit_additions")
        .insert(creditAdditionPayload)
        .select()
        .single();

    if (creditError) {
        console.error("❌ Erro ao registrar a adição de crédito:", creditError);
        // Cenário para rollback
        throw new Error(creditError.message);
    }

    console.log(`✅ ${sessionsAdded} sessões adicionadas com sucesso ao pacote ${packageId}.`);

    return newCreditAddition as FinancialTypes.PackageCreditAddition;
};

export const PackageService = {
  createPackage,
  updatePackage,
  consumeSession,
  addCredits,
};
